import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import * as styles from './companies.styles';
import { deleteCompanyById } from './services/companies.service';
import CompanyInfoGroup from './groups/company-info-group.component';
import CompanyOfficesGroup from './groups/company-offices-group.component';
import { useCompany } from './hooks/companies.hook';
import CompanyConfirmationModal from './modals/company-confirmation-modal.component';
import BackButton from '../../core/components/back-button/back-button.component';
import PrimaryButton from '../../core/components/buttons/primary-button.component';
import Column from '../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../core/components/flex/flex.component';
import notify from '../../core/components/notify/notify.component';
import Row from '../../core/components/row/row.component';
import { IsMobileContext } from '../../core/context/is-mobile';
import CommonStyles from '../../core/common.styles';
import RouteConstants from '../../core/routes.constants';
import { useKeycloak } from '../authentication/keycloak/KeycloakProvider';

const CompanyEdit = (): React.JSX.Element => {
  const navigate = useNavigate();
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const { id: companyId } = useParams<{ id: string }>();
  const keycloak = useKeycloak();

  const { company, refresh } = useCompany({ id: companyId });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const navigateToOverview = useCallback((): void => {
    navigate(`/${RouteConstants.PATH_COMPANIES}`);
  }, [navigate]);

  const [isDeleting, setIsDeleting] = useState(false);

  // Exactly one message either way: the service reports nothing itself, so a failure is not toasted twice.
  const deleteCompany = useCallback(async (acknowledged: boolean): Promise<void> => {
    if (!companyId) {
      notify(t('delete-company-error-message'), false);
      return;
    }

    setIsDeleting(true);
    const outcome = await deleteCompanyById(companyId, keycloak, acknowledged);
    setIsDeleting(false);
    // Closed after a failure too, so opening it again re-reads what the deletion would cancel.
    setShowDeleteModal(false);

    if (outcome.deleted) {
      notify(t('delete-company-success-message'), true);
      navigateToOverview();
    } else {
      notify(t(outcome.errorKey ?? 'delete-company-error-message'), false);
    }
  }, [companyId, navigateToOverview, keycloak, t]);

  return (
    <div className={style(styles.bottomMargin)}>
      {isMobile ? (
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch} className={style(styles.cornerButton(isMobile))} itemSpacing={12}>
          <BackButton title={t('companies')} navigate={navigateToOverview} />
          <Row itemSpacing={12}>
            <PrimaryButton onClick={navigateToOverview}>{t('cancel')}</PrimaryButton>
          </Row>
        </Column>
      ) : (
        <Row className={style(styles.cornerButton(isMobile))} mainAxisAlignment={MainAxisAlignment.SpaceBetween}>
          <BackButton title={t('companies')} navigate={navigateToOverview} />
          <Row itemSpacing={12} crossAxisAlignment={CrossAxisAlignment.Center}>
            <PrimaryButton onClick={navigateToOverview}>{t('cancel')}</PrimaryButton>
          </Row>
        </Row>
      )}
      <div className={style(CommonStyles.bodyMargin(isMobile))}>
        <CompanyInfoGroup isCreatingCompany={false} companyId={companyId} company={company} onSaved={refresh} />

        <CompanyOfficesGroup offices={company?.offices} />

        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-company')}</span>
          <DeleteIcon className={style(styles.deleteIcon)} />
        </Row>
        <CompanyConfirmationModal
          showModal={showDeleteModal}
          isLoading={isDeleting}
          companyId={companyId}
          itemName={company?.name ?? t('unknown').toString()}
          offices={company?.offices}
          handleAccept={(acknowledged): void => {
            deleteCompany(acknowledged);
          }}
          handleClose={(): void => {
            setShowDeleteModal(false);
          }}
        />
      </div>
    </div>
  );
};

export default CompanyEdit;
